import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth, storage } from '../firebase';
import { updatePassword, verifyBeforeUpdateEmail, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  User, Save, LogOut, X,
  Eye, EyeOff, Mail, Camera, Trash2, KeyRound,
} from 'lucide-react';
import { updateAccount } from '../services/firestoreService';
import StartSidebar from '../components/StartSidebar';
import PortalBackground from '../components/PortalBackground';

// Shared visual styles — same design language as the portal's My Profile page
// (src/pages/MyProfile.js), so a lead's profile and a client's profile match.
const inputBase = 'w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';
const labelBase = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5';
const sectionCard = 'rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 mb-6';

const SectionHeading = ({ emoji, children }) => (
  <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-6">
    <span className="text-lg leading-none">{emoji}</span>
    {children}
  </h2>
);

const Toggle = ({ checked, onChange, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${
      checked ? 'bg-red-600' : 'bg-gray-200 dark:bg-gray-700'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-[22px]' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const StartProfile = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [emailChangeData, setEmailChangeData] = useState({
    newEmail: '',
    password: ''
  });
  const [emailPasswordVisible, setEmailPasswordVisible] = useState(false);
  const [emailModalError, setEmailModalError] = useState('');
  const fileInputRef = useRef(null);
  
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    website: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [accountId, setAccountId] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const authEmail = currentUser.email || userData.email || '';
          setProfileData(prev => ({
            ...prev,
            ...userData,
            email: authEmail
          }));
          setProfileImage(userData.profileImage || null);
          const accountIdFromUser = userData.accountId || userData.clientId;
          if (accountIdFromUser) {
            setAccountId(accountIdFromUser);
          }
          // If Auth email was updated (e.g. after clicking email-change link), sync to Firestore and account
          if (currentUser.email && userData.email !== currentUser.email) {
            try {
              const userRef = doc(db, 'users', currentUser.uid);
              await updateDoc(userRef, { email: currentUser.email, updatedAt: new Date() });
              if (accountIdFromUser) {
                await updateAccount(accountIdFromUser, {
                  contact: { ...(userData.contact || {}), email: currentUser.email },
                  email: currentUser.email
                });
              }
            } catch (syncErr) {
              console.error('Error syncing email to profile/account:', syncErr);
            }
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setMessage({ type: 'error', text: 'Failed to load profile data' });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setProfileData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 5MB' });
      return;
    }

    try {
      setSaving(true);
      const imageRef = ref(storage, `profile-images/${currentUser.uid}`);
      
      // Delete old image if exists
      if (profileImage) {
        try {
          await deleteObject(ref(storage, profileImage));
        } catch (error) {
          console.log('No previous image to delete');
        }
      }

      const snapshot = await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      setProfileImage(downloadURL);
      setMessage({ type: 'success', text: 'Profile image updated successfully' });
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: 'Failed to upload image' });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) return;

    try {
      setSaving(true);
      const userRef = doc(db, 'users', currentUser.uid);
      
      const updateData = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        company: profileData.company,
        phone: profileData.phone,
        website: profileData.website,
        address: profileData.address,
        profileImage: profileImage,
        updatedAt: new Date()
      };

      await updateDoc(userRef, updateData);

      // Sync contact information to the linked account if accountId exists
      if (accountId) {
        try {
          const accountUpdateData = {
            firstName: profileData.firstName,
            lastName: profileData.lastName,
            company: profileData.company,
            companyName: profileData.company,
            phone: profileData.phone,
            phoneNumber: profileData.phone,
            website: profileData.website,
            streetAddress: profileData.address.street,
            city: profileData.address.city,
            state: profileData.address.state,
            zipCode: profileData.address.zipCode,
            country: profileData.address.country,
            contact: {
              firstName: profileData.firstName,
              lastName: profileData.lastName,
              phone: profileData.phone,
              email: currentUser.email || profileData.email
            }
          };

          await updateAccount(accountId, accountUpdateData);
          console.log('Successfully synced profile to account:', accountId);
        } catch (accountError) {
          console.error('Error syncing to account:', accountError);
          // Don't show error to user as profile was saved successfully
        }
      }

      setMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    try {
      setSaving(true);
      await updatePassword(currentUser, passwordData.newPassword);
      setMessage({ type: 'success', text: 'Password updated successfully' });
      setShowPasswordModal(false);
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error updating password:', error);
      setMessage({ type: 'error', text: 'Failed to update password' });
    } finally {
      setSaving(false);
    }
  };

  const handleEmailChange = async () => {
    const newEmail = (emailChangeData.newEmail || '').trim().toLowerCase();
    const password = emailChangeData.password;

    if (!newEmail) {
      setEmailModalError('Please enter a new email address');
      return;
    }
    if (newEmail === (currentUser?.email || '').toLowerCase()) {
      setEmailModalError('New email is the same as your current email');
      return;
    }
    if (!password) {
      setEmailModalError('Please enter your current password to confirm your identity');
      return;
    }

    try {
      setSaving(true);
      setEmailModalError('');

      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);

      // Same pattern as change password: reauth then one call. This sends a link to the new email;
      // when they click it, Firebase updates the email (no immediate update).
      const actionCodeSettings = {
        url: window.location.origin + '/login?emailChanged=1',
        handleCodeInApp: false
      };
      await verifyBeforeUpdateEmail(currentUser, newEmail, actionCodeSettings);

      setMessage({ type: 'success', text: `We've sent a confirmation link to ${newEmail}. Click the link in that email to complete the change. Your email will stay the same until you click it.` });
      setShowEmailModal(false);
      setEmailChangeData({ newEmail: '', password: '' });
    } catch (error) {
      console.error('Error updating email:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setEmailModalError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/email-already-in-use') {
        setEmailModalError('This email is already in use by another account.');
      } else if (error.code === 'auth/requires-recent-login') {
        setEmailModalError('Please sign in again, then try changing your email.');
      } else {
        setEmailModalError(error.message || 'Failed to update email');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleDeleteImage = async (e) => {
    e.stopPropagation();
    if (!profileImage) return;

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      await deleteObject(ref(storage, `profile-images/${currentUser.uid}`));
      await updateDoc(doc(db, 'users', currentUser.uid), {
        profileImage: null,
        lastUpdated: new Date().toISOString()
      });

      setProfileImage(null);
      setMessage({ type: 'success', text: 'Profile picture removed successfully!' });
    } catch (error) {
      console.error('Error deleting image:', error);
      setMessage({ type: 'error', text: 'Failed to remove profile picture. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen relative w-full overflow-x-hidden px-4 py-6 md:p-8">
        {/* Same ambient BBA background the Start Dashboard shows */}
        <PortalBackground />
        <StartSidebar />
        <div className="relative max-w-4xl mx-auto w-full">
          <div className="text-center mb-6 animate-pulse">
            <div className="h-7 w-40 mx-auto rounded bg-gray-100 dark:bg-gray-800"></div>
            <div className="h-3 w-64 mx-auto mt-2 rounded bg-gray-100 dark:bg-gray-800"></div>
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 mb-6 animate-pulse">
              <div className="h-4 w-44 rounded bg-gray-100 dark:bg-gray-800 mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[0, 1, 2, 3].map((j) => (
                  <div key={j} className="space-y-2">
                    <div className="h-3 w-24 rounded bg-gray-100 dark:bg-gray-800"></div>
                    <div className="h-10 rounded-lg bg-gray-100 dark:bg-gray-800"></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative w-full overflow-x-hidden px-4 py-6 md:p-8">
      {/* Same ambient BBA background the Start Dashboard shows */}
      <PortalBackground />
      <StartSidebar />
      <div className="relative max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-6 px-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Profile</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage your personal info, company details, and preferences
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl border text-sm font-medium ${
            message.type === 'success'
              ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800'
              : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Personal Information */}
        <div className={sectionCard}>
          <SectionHeading emoji="👤">Personal Information</SectionHeading>

          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            {/* Profile Picture */}
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div
                className="relative w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden cursor-pointer ring-2 ring-gray-200 dark:ring-gray-700 hover:ring-red-300 dark:hover:ring-red-700 transition-all group"
                onClick={() => fileInputRef.current?.click()}
              >
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-full h-full text-gray-300 dark:text-gray-600 p-4" />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="w-5 h-5 text-white" />
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Profile Picture</span>
              {profileImage && (
                <button
                  className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors"
                  onClick={handleDeleteImage}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove
                </button>
              )}
            </div>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelBase}>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={profileData.firstName}
                  onChange={handleInputChange}
                  className={inputBase}
                  placeholder="Enter your first name"
                />
              </div>

              <div>
                <label className={labelBase}>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={profileData.lastName}
                  onChange={handleInputChange}
                  className={inputBase}
                  placeholder="Enter your last name"
                />
              </div>

              <div>
                <label className={labelBase}>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleInputChange}
                  className={inputBase}
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className={labelBase}>Email Address</label>
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    disabled
                    placeholder="Email address"
                    className="flex-1 min-w-0 px-3 py-2.5 bg-transparent border-0 rounded-lg text-sm text-gray-500 dark:text-gray-400 cursor-not-allowed focus:ring-0 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEmailModal(true)}
                    className="text-sm font-medium px-3 py-2 rounded-lg shrink-0 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className={sectionCard}>
          <SectionHeading emoji="🏢">Company Information</SectionHeading>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelBase}>Company Name</label>
              <input
                type="text"
                name="company"
                value={profileData.company}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your company name"
              />
            </div>

            <div>
              <label className={labelBase}>Website</label>
              <input
                type="url"
                name="website"
                value={profileData.website}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="https://yourcompany.com"
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelBase}>Street Address</label>
              <input
                type="text"
                name="address.street"
                value={profileData.address.street}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your street address"
              />
            </div>

            <div>
              <label className={labelBase}>City</label>
              <input
                type="text"
                name="address.city"
                value={profileData.address.city}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your city"
              />
            </div>

            <div>
              <label className={labelBase}>State/Province</label>
              <input
                type="text"
                name="address.state"
                value={profileData.address.state}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your state or province"
              />
            </div>

            <div>
              <label className={labelBase}>ZIP/Postal Code</label>
              <input
                type="text"
                name="address.zipCode"
                value={profileData.address.zipCode}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your ZIP or postal code"
              />
            </div>

            <div>
              <label className={labelBase}>Country</label>
              <input
                type="text"
                name="address.country"
                value={profileData.address.country}
                onChange={handleInputChange}
                className={inputBase}
                placeholder="Enter your country"
              />
            </div>
          </div>
        </div>

        {/* Style Preferences */}
        <div className={sectionCard}>
          <SectionHeading emoji="🎨">Style Preferences</SectionHeading>
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-lg shrink-0">
                🌙
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Dark Mode</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Switch to a darker color palette</p>
              </div>
            </div>
            <Toggle checked={darkMode} onChange={toggleDarkMode} ariaLabel="Toggle dark mode" />
          </div>
        </div>

        {/* Security */}
        <div className={sectionCard}>
          <SectionHeading emoji="🔐">Security</SectionHeading>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center shrink-0 shadow-sm">
                <KeyRound className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Password</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Choose a new password for your account</p>
              </div>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors shrink-0"
            >
              <KeyRound className="w-4 h-4" />
              Change Password
            </button>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold shadow-sm transition-colors"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>

        {/* Email Change Modal */}
        {showEmailModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowEmailModal(false); setEmailChangeData({ newEmail: '', password: '' }); setEmailModalError(''); setMessage({ type: '', text: '' }); }}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => { setShowEmailModal(false); setEmailChangeData({ newEmail: '', password: '' }); setEmailModalError(''); setMessage({ type: '', text: '' }); }}
                  className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-2">
                    <Mail className="w-5 h-5" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Email address change</h2>
                </div>
              </div>

              <div className="p-5">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  For security, when you change your email address, we&apos;ll send a message to your current email so you can review the change.
                </p>

                {emailModalError && (
                  <div className="mb-4 p-3 rounded-xl border text-sm bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800">
                    {emailModalError}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className={labelBase}>New email address</label>
                    <input
                      type="email"
                      value={emailChangeData.newEmail}
                      onChange={(e) => setEmailChangeData(prev => ({ ...prev, newEmail: e.target.value }))}
                      placeholder="you@example.com"
                      className={inputBase}
                    />
                  </div>

                  <div>
                    <label className={labelBase}>Current password</label>
                    <div className="relative">
                      <input
                        type={emailPasswordVisible ? 'text' : 'password'}
                        value={emailChangeData.password}
                        onChange={(e) => setEmailChangeData(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Enter your password to confirm"
                        className={`${inputBase} pr-10`}
                      />
                      <button
                        type="button"
                        onClick={() => setEmailPasswordVisible(prev => !prev)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label={emailPasswordVisible ? 'Hide password' : 'Show password'}
                      >
                        {emailPasswordVisible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowEmailModal(false); setEmailChangeData({ newEmail: '', password: '' }); setEmailModalError(''); setMessage({ type: '', text: '' }); }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleEmailChange}
                    disabled={saving}
                    className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? 'Updating...' : 'Update email'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowPasswordModal(false); setPasswordData({ newPassword: '', confirmPassword: '' }); }}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative p-5 border-b border-gray-200 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => { setShowPasswordModal(false); setPasswordData({ newPassword: '', confirmPassword: '' }); }}
                  className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
                    <KeyRound className="w-5 h-5" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Change Password</h2>
                </div>
              </div>

              <div className="p-5">
                <div className="mb-4">
                  <label className={labelBase}>New Password</label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    className={inputBase}
                    placeholder="Enter new password"
                  />
                </div>

                <div className="mb-6">
                  <label className={labelBase}>Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className={inputBase}
                    placeholder="Confirm new password"
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowPasswordModal(false); setPasswordData({ newPassword: '', confirmPassword: '' }); }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handlePasswordChange}
                    disabled={saving}
                    className="px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartProfile;
